import { useEffect, useState } from "react";
import { useNavigate, Navigate } from "react-router-dom";
import { ArrowLeft, ArrowRight, Loader2 } from "lucide-react";
import { api, errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useSpots } from "@/components/site/SpotsMeter";
import { StepBusiness, StepCoverage, StepCredentials, StepGoals } from "@/components/site/IntakeSteps";
import { WaitlistForm } from "@/pages/Waitlist";
import { toast } from "sonner";

const STEPS = ["Business profile", "Coverage", "Credentials", "Goals"];
const BLANK = {
  company_name: "", contact_name: "", title: "", business_email: "", phone: "", website: "", city: "", state: "",
  states_served: [], nationwide: false, services: [], metros_served: [], years_in_business: 0, exchanges_per_year: "", bio: "",
  logo_path: null, fea_member: false, fidelity_bond: false, bond_amount: "", eo_insurance: false,
  segregated_accounts: false, certifications: [], business_goals: [], goals_notes: "", ideal_client: "", referral_source: "",
};

const validate = (step, f) => {
  if (step === 0) {
    if (f.company_name.trim().length < 2) return "Company name is required";
    if (f.contact_name.trim().length < 2) return "Primary contact is required";
    if (!/\S+@\S+\.\S+/.test(f.business_email)) return "A valid business email is required";
    if (!f.state) return "Select your headquarters state";
    if (f.bio.trim().length < 20) return "Bio must be at least 20 characters";
  }
  if (step === 1 && f.services.length === 0) return "Pick at least one exchange type";
  return null;
};

export default function Intake() {
  const { user, profile, partner, setProfile } = useAuth();
  const spots = useSpots();
  const navigate = useNavigate();
  const [step, setStep] = useState(0);
  const [f, setF] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (profile === null || f) {
      return;
    }
    const base = { ...BLANK, contact_name: user?.name || "", business_email: user?.email || "" };
    setF(profile ? { ...base, ...Object.fromEntries(Object.keys(BLANK).map((k) => [k, profile[k] ?? BLANK[k]])) } : base);
  }, [profile, user, f]);

  if (!f || !spots) return <div className="py-32 flex justify-center"><Loader2 className="animate-spin text-qi-pink" /></div>;

  if (partner) return <Navigate to="/dashboard" replace />;
  if (!profile && spots.remaining === 0) {
    return (
      <div className="max-w-xl mx-auto px-6 py-20" data-testid="intake-full">
        <span className="eyebrow">All founding spots claimed</span>
        <h1 className="font-display text-4xl font-black mt-4">Paid plan coming soon</h1>
        <p className="text-white/55 mt-3">All {spots.total} free spots are taken. Join the waitlist and we'll invite you when paid listings open.</p>
        <div className="glass-card p-8 mt-8"><WaitlistForm /></div>
      </div>
    );
  }

  const set = (k) => (e) => setF({ ...f, [k]: k === "years_in_business" ? Number(e.target.value) : e.target.value });
  const setVal = (k, v) => setF({ ...f, [k]: v });
  const next = () => {
    const err = validate(step, f);
    if (err) return toast.error(err);
    setStep(step + 1);
  };
  const submit = async () => {
    const err = validate(0, f) || validate(1, f);
    if (err) return toast.error(err);
    setBusy(true);
    const codes = new Set([f.state, ...f.states_served]);
    const body = { ...f, metros_served: f.metros_served.filter((m) => f.nationwide || codes.has(m.slice(-2))) };
    try {
      const { data } = await api.put("/me/profile", body);
      setProfile(data);
      toast.success(profile ? "Profile updated" : "Intake submitted — we'll review your listing shortly");
      navigate("/dashboard");
    } catch (ex) {
      toast.error(errMsg(ex));
    } finally {
      setBusy(false);
    }
  };
  const Step = [StepBusiness, StepCoverage, StepCredentials, StepGoals][step];

  return (
    <div className="max-w-3xl mx-auto px-6 py-14" data-testid="intake-page">
      <span className="eyebrow">{profile ? "Edit your listing" : "QI intake"}</span>
      <h1 className="font-display text-4xl font-black tracking-tight mt-4">{profile ? "Update your firm profile" : "Claim your founding listing"}</h1>
      <div className="grid grid-cols-4 gap-2 mt-8">
        {STEPS.map((s, i) => (
          <button key={s} type="button" onClick={() => i < step && setStep(i)} data-testid={`intake-step-tab-${i}`} className="text-left">
            <div className={`h-1.5 rounded-full ${i <= step ? "bg-qi-pink" : "bg-white/10"}`} />
            <span className={`block mt-2 text-[11px] font-mono uppercase tracking-wider ${i === step ? "text-white" : "text-white/40"}`}>{i + 1}. {s}</span>
          </button>
        ))}
      </div>
      <div className="glass-card p-6 md:p-8 mt-8"><Step f={f} set={set} setVal={setVal} /></div>
      <div className="flex justify-between mt-6">
        <button type="button" disabled={step === 0} onClick={() => setStep(step - 1)} className="btn-ghost disabled:opacity-30" data-testid="intake-step-back-button"><ArrowLeft size={16} /> Back</button>
        {step < STEPS.length - 1 ? (
          <button type="button" onClick={next} className="btn-pink" data-testid="intake-step-next-button">Next <ArrowRight size={16} /></button>
        ) : (
          <button type="button" onClick={submit} disabled={busy} className="btn-pink" data-testid="intake-submit-button">{busy ? "Saving..." : profile ? "Save changes" : "Submit for review"}</button>
        )}
      </div>
    </div>
  );
}
